import React, { useCallback, useEffect, useState } from "react";
import Modal from "components/Modal/Modal";
import { useMutation } from "@apollo/client";
import { sendPhoneVerificationCodeMutation } from "./mutations";
import Typography from "components/Typography";
import { SpinnerBack } from "components/Spinner/Spinner";
import { useUserPhoneVerify } from "./hooks";
import classes from "./MembershipValidationModal.module.css";
import NotificationPopover from "components/NotificationPopover/NotificationPopover";
import { MembershipValidationPhone } from "./components/MembershipValidationPhone";
import { MembershipValidationCode } from "./components/MembershipValidationCode";
import cn from "classnames";
import {
  extractSuccess,
  extractServerErrorMessage
} from "utils/extractErrors";

const extractPhoneVerificationCodeSuccess = extractSuccess("send_phone_verification_code");
const extractPhoneVerificationCodeErrorMessage = extractServerErrorMessage("send_phone_verification_code");

export const MembershipValidationModal = ({ show, onClose, onVerified, clubId }) => {
  const {
    hasUserPhone,
    phoneNumber: phone,
    isLoaded,
    loading
  } = useUserPhoneVerify();

  const [error, setError] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [isPhoneEdit, setIsPhoneEdit] = useState(false);

  const [sendPhoneVerificationCodeAction, { loading: sendPhoneVerificationCodeLoading }] = useMutation(sendPhoneVerificationCodeMutation, {
    fetchPolicy: "no-cache",
    onCompleted: (response) => {
      if(!extractPhoneVerificationCodeSuccess({ data: response })) {
        const errorText = extractPhoneVerificationCodeErrorMessage({ data: response });
        setError(errorText);
      }
    }
  });

  const onChangePhone = useCallback(
    (value) => {
      setPhoneNumber(value);
    },
    [setPhoneNumber]
  );

  useEffect(() => {
    if (isLoaded) {
      setPhoneNumber(phone);
      setIsPhoneEdit(!hasUserPhone);

      if (hasUserPhone) {
        sendPhoneVerificationCodeAction();
      }
    }
  }, [hasUserPhone, isLoaded, phone, sendPhoneVerificationCodeAction]);

  return (
    <Modal
      show={show}
      onClose={onClose}
      modalWrapClassnames={cn('mdl-wrap--full-screen-less-768', classes.modalWrap)}
      modalClassnames={classes.modal}
      modalCrossButtonClassname={classes.crossButton}
    >
      {(loading || sendPhoneVerificationCodeLoading) && <SpinnerBack />}
      <div className={classes.container}>
        <Typography
          variant="headline"
          size="m"
          weight="bold"
          className={classes.title}
        >
          Membership Validation
        </Typography>
        <Typography
          variant="subtitle"
          size="s"
          className="mt-16 mt-m-20"
        >
          Please provide your cell phone number below to receive a one-time verification code.
        </Typography>

        <MembershipValidationPhone
          isPhoneEdit={isPhoneEdit}
          setIsPhoneEdit={setIsPhoneEdit}
          onChangePhone={onChangePhone}
          phoneNumber={phoneNumber}
          onClose={onClose}
        />

        {!isPhoneEdit ? (
          <div className="mt-20">
            <MembershipValidationCode
              phoneNumber={phoneNumber}
              clubId={clubId}
              onVerified={onVerified}
            />
          </div>
        ) : null}

        {error?.length !== 0 && (
          <div className="t-8 b-8">
            <NotificationPopover
              show={true}
              status="error"
              text={error}
              onClose={() => {
                setError('');
              }}
            />
          </div>
        )}
      </div>
    </Modal>
  );
};
